import React, {useState, useRef} from 'react';
import {styled} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Image from '@jobseeker/lotus/components/Image';
import {useWrappedTheme} from '@jobseeker/lotus/hooks';

const BoxContainer = styled(Box)``;

const LabelContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TitleContainer = styled(Typography)`
  width: auto;
  height: auto;
  min-height: 19px;
  font-family: Nunito Sans;
  font-size: 14px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: #000;
  opacity: 0.8;
`;

const SubTitleContainer = styled(Typography, {
  shouldForwardProp: prop => prop !== 'id',
})<{id?: string}>(({id}) => ({
  fontSize: '13px',
  opacity: 0.8,
  display: id === 'subtitle-top' ? 'block' : 'none',
  '@media (max-width: 480px)': {
    margin: '9px 0 0',
    display: id === 'subtitle-bottom' ? 'block' : 'none',
  },
}));

const FileContainer = styled(Box)`
  width: auto;
  height: auto;
  min-height: 60px;
  margin-top: 5px;
  padding: 8px;
  border-radius: 4px;
  border: solid 2px #e6e6e6;
  background-color: #fff;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  position: relative;
  border-style: dotted;
  FileSelectButton @media (max-width: 480px) {
    margin: 11px 0 9px 1px;
    padding: 10px;
  }
`;

const FileSelectContainer = styled(Box)`
  width: auto;
  height: auto;
  min-height: 43px;
  padding: 12px 15px 11px 14px;
  border-radius: 6px;
  background: #f2f5fa;
  @media (max-width: 480px) {
    min-height: 40px;
    padding: 11px 15px 9px 10px;
  }
`;

const InputContainer = styled('input')`
  display: none;
`;
const FileSelectButton = styled(Box)`
  outline: none !important;
  border: none !important;
  background: inherit;
  cursor: pointer;
  display: flex;
`;

const UploadIcon = styled(Image)`
  width: 18px;
  height: 18.1px;
  margin: 0 0 1.9px 0;
  opacity: 0.7;
`;

const UploadText = styled(Typography)`
  width: auto;
  height: auto;
  min-height: 19px;
  margin: 1px 0 0 10px;
  opacity: 0.7;
  font-family: Nunito Sans;
  font-size: 14px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: #000;
  @media (max-width: 480px) {
    margin: 1px 0 0 9px;
  }
`;

const ChangeFileText = styled(Typography)`
  width: auto;
  height: auto;
  font-family: Nunito Sans;
  font-size: 14px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: theme.palette.primary.main;
  position: absolute;
  right: 0;
  margin-right: 8px;
  @media (max-width: 480px) {
    margin-right: 10px;
  }
`;

const ErrorContainer = styled(Typography)`
  width: auto;
  height: auto;
  min-height: 18px;
  margin: 8px 0 0;
  font-family: Nunito Sans;
  font-size: 13px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: #eb1c2a;
`;

const AllowedFileTypes = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf',
  'application/msword',
];

interface FileUploadProps {
  Title: React.ReactNode;
  SubTitle?: string;
  SelectText: string;
  FileSizeLimit: number;
  FileLimitError: string;
  FileReceiver?: (file: Blob) => void;
  resumeNameLimit?: number;
}

const FileUpload: React.FC<FileUploadProps> = ({
  Title,
  SubTitle,
  SelectText,
  FileSizeLimit,
  FileLimitError,
  FileReceiver,
  resumeNameLimit = 17,
}) => {
  const theme = useWrappedTheme();
  const [selectedFile, setSelectedFile] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const fileSelected = files[0];
      if (
        !AllowedFileTypes.includes(fileSelected.type) ||
        fileSelected.size > FileSizeLimit * 1024 * 1024
      ) {
        setSelectedFile(null);
        setError(FileLimitError);
        return;
      }
      setError(null);
      setSelectedFile(fileSelected);
      FileReceiver && FileReceiver(fileSelected);
    }
  };

  return (
    <BoxContainer>
      <LabelContainer>
        <TitleContainer>{Title}</TitleContainer>
        <SubTitleContainer id="subtitle-top">{SubTitle}</SubTitleContainer>
      </LabelContainer>
      <FileContainer>
        <FileSelectContainer>
          <InputContainer
            ref={fileInputRef}
            type="file"
            accept={AllowedFileTypes.join(',')}
            onChange={handleFileSelect}
          />
          <FileSelectButton onClick={() => fileInputRef.current?.click()}>
            <UploadIcon src="https://hirist-assets.s3.ap-south-1.amazonaws.com/icons/file-upload.png" />
            <UploadText>
              {selectedFile
                ? window.innerWidth <= 480 &&
                  selectedFile.name.length > resumeNameLimit
                  ? `${selectedFile.name.substring(0, resumeNameLimit)}...`
                  : selectedFile.name
                : SelectText}
            </UploadText>
            {selectedFile && <ChangeFileText>Change</ChangeFileText>}
          </FileSelectButton>
        </FileSelectContainer>
      </FileContainer>
      <SubTitleContainer id="subtitle-bottom">{SubTitle}</SubTitleContainer>
      {error && <ErrorContainer>{error}</ErrorContainer>}
    </BoxContainer>
  );
};

export default FileUpload;
